import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { dayLabel, pctOf, RANGE, type SeriesPoint, tone, VIEW_W, xOf } from '../lib/radar-trends';
import { decimal } from '../lib/time';
import { AdminTrendDelta } from './AdminTrendDelta';

// The radar chart's crosshair readout (#6715): the day, its value, the change from
// the previous stored day, and the change from that day to today. From `md` it sits
// beside the crosshair at `--tip-x`, flipping to its left past 70% of the width;
// below `md` no side of the plot has room, so the stylesheet ignores `--tip-x` and
// docks it above the plot instead of over the day it describes (#6940, `_admin.scss`).
// The first point has no previous day in the window, so that row is left out.

export function AdminTrendTooltip(props: {
  series: readonly SeriesPoint[];
  index: number;
  inverse: boolean;
  today: string;
}): JSX.Element {
  const point = () => props.series[props.index] as SeriesPoint;
  const prev = () => props.series[props.index - 1];
  const now = () => (props.series[props.series.length - 1] as SeriesPoint).value;
  const flip = () => point().ago < RANGE * 0.3;
  // The readout is `--tip-w` wide (`_admin.scss`), so the clamp can keep it whole.
  const left = () => {
    const x = pctOf(xOf(point().ago), VIEW_W);
    const beside = flip()
      ? `calc(${x} - var(--tip-gap) - var(--tip-w))`
      : `calc(${x} + var(--tip-gap))`;
    return `clamp(0px, ${beside}, calc(100% - var(--tip-w)))`;
  };
  return (
    <div
      class="admin-trend-chart__tip"
      data-side={flip() ? 'left' : 'right'}
      style={{ '--tip-x': left() }}
    >
      <span class="admin-trend-chart__tip-day">{dayLabel(point().day, props.today)}</span>
      <span class="admin-trend-chart__tip-value">{decimal(point().value)}</span>
      <Show when={prev()}>
        {(p) => (
          <span class="admin-trend-chart__tip-row">
            <AdminTrendDelta
              delta={point().value - p().value}
              tone={tone(point().value - p().value, props.inverse)}
            />
            <span class="admin-trend__muted">
              {t('admin.trend_vs', { date: dayLabel(p().day, props.today) })}
            </span>
          </span>
        )}
      </Show>
      <span class="admin-trend-chart__tip-row">
        <AdminTrendDelta
          delta={now() - point().value}
          tone={tone(now() - point().value, props.inverse)}
        />
        <span class="admin-trend__muted">{t('admin.trend_to_today')}</span>
      </span>
    </div>
  );
}
